import { TICK_MS, WELCOME_BACK_MIN_MS } from '../data/balance';
import { applyOffline, type OfflineReport } from '../core/offline';
import { drainMessages, touch } from '../core/state';
import { tick } from '../core/tick';
import type { GameMessage, GameState, SimCtx } from '../core/types';
import { loadGameState, loadPrefs, savePrefs, saveGameState, type Prefs } from '../platform/storage';
import { track } from '../platform/telemetry';

/**
 * Owns the game state and the two loops:
 *  - sim: fixed 100 ms steps, driven from requestAnimationFrame with an
 *    accumulator (so a hidden tab — no rAF — pauses the world, by design);
 *  - save: every 10 s, on purchases (debounced) and when the page hides.
 * Returning to the tab after ≥5 s pays offline income (same model as a reload).
 */

export type MessageHandler = (m: GameMessage) => void;
export type WelcomeHandler = (r: OfflineReport) => void;

const SAVE_EVERY_MS = 10_000;
const MAX_TICKS_PER_FRAME = 600;

class Controller {
  state: GameState;
  prefs: Prefs;
  /** Dev-panel speed multiplier. */
  speed = 1;
  loadError: string | null;
  fresh: boolean;
  private acc = 0;
  private lastPerf = 0;
  private lastSave = 0;
  private saveSoonAt = 0;
  private onMessage: MessageHandler = () => {};
  private onWelcome: WelcomeHandler = () => {};

  constructor() {
    const loaded = loadGameState();
    this.state = loaded.state;
    this.fresh = loaded.fresh;
    this.loadError = loaded.error;
    this.prefs = loadPrefs();
  }

  ctx(): SimCtx {
    const wall = Date.now();
    return { wall, localHour: new Date(wall).getHours() };
  }

  /** Interpolated sim time for smooth rendering between 100 ms steps. */
  simNow(): number {
    return this.state.time + Math.min(TICK_MS, this.acc);
  }

  viewFlock() {
    return this.state.flocks.find((f) => f.id === this.state.viewFlock) ?? this.state.flocks[0]!;
  }

  setView(flockId: number): void {
    this.state.viewFlock = flockId;
  }

  handlers(onMessage: MessageHandler, onWelcome: WelcomeHandler): void {
    this.onMessage = onMessage;
    this.onWelcome = onWelcome;
  }

  /** Called once at startup: pays out time away since the last save. */
  start(): void {
    this.lastPerf = performance.now();
    if (!this.fresh) this.resume();
    this.state.sessionStartWall = Date.now();
    this.state.wallAt = Date.now();
    this.routeMessages();
  }

  /** Offline catch-up (page load or tab return). */
  resume(): void {
    const now = Date.now();
    const away = now - this.state.wallAt;
    this.acc = 0;
    this.lastPerf = performance.now();
    if (away >= 60_000) this.state.sessionStartWall = now;
    const report = applyOffline(this.state, this.ctx(), away);
    this.state.wallAt = now;
    if (report && away >= WELCOME_BACK_MIN_MS) {
      track({ type: 'welcome_back', awayMs: away, earned: report.earned, capped: report.capped });
      this.onWelcome(report);
    }
    this.save();
  }

  frame(perfNow: number): void {
    const dt = Math.min(1000, Math.max(0, perfNow - this.lastPerf));
    this.lastPerf = perfNow;
    this.acc += dt * this.speed;
    let n = 0;
    while (this.acc >= TICK_MS && n < MAX_TICKS_PER_FRAME) {
      tick(this.state, this.ctx());
      this.acc -= TICK_MS;
      n++;
    }
    if (n >= MAX_TICKS_PER_FRAME) this.acc = 0;
    this.routeMessages();
    const now = Date.now();
    if (now - this.lastSave >= SAVE_EVERY_MS || (this.saveSoonAt && now >= this.saveSoonAt)) this.save();
  }

  routeMessages(): void {
    for (const m of drainMessages(this.state)) this.onMessage(m);
  }

  /** After a purchase or other meaningful change. */
  changed(): void {
    touch(this.state);
    if (!this.saveSoonAt) this.saveSoonAt = Date.now() + 1500;
    this.routeMessages();
  }

  save(): void {
    this.lastSave = Date.now();
    this.saveSoonAt = 0;
    this.state.wallAt = Date.now();
    saveGameState(this.state);
  }

  setPrefs(patch: Partial<Prefs>): void {
    this.prefs = { ...this.prefs, ...patch };
    savePrefs(this.prefs);
  }

  replaceState(next: GameState): void {
    this.state = next;
    this.acc = 0;
    this.state.wallAt = Date.now();
    this.save();
  }
}

export const game = new Controller();
